import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useUpdateLeadDetails } from '@/hooks/useLeadActions';
import { useAddLeadActivity } from '@/hooks/useLeadActivities';
import { addDays } from 'date-fns';
import { Clock, Calendar } from 'lucide-react';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';

interface FollowUpDialogProps {
  leadId: string;
  firstFollowUpAt?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Reopening a lost lead: clear its lost reason along with the status change */
  clearLostReason?: boolean;
}

export function FollowUpDialog({ leadId, firstFollowUpAt, open, onOpenChange, clearLostReason }: FollowUpDialogProps) {
  const [description, setDescription] = useState('');
  const [selectedDays, setSelectedDays] = useState<number | null>(null);
  const { now } = useTimezoneUtils();
  
  const updateLead = useUpdateLeadDetails();
  const addActivity = useAddLeadActivity();
  
  const isSubmitting = updateLead.isPending || addActivity.isPending;

  const handleSubmit = () => {
    if (!description.trim() || selectedDays === null) return;
    
    const currentTime = now();
    const followUpDate = addDays(currentTime, selectedDays);
    
    // Add activity first
    addActivity.mutate({
      leadId,
      activityType: 'call',
      description: description.trim(),
      metadata: {
        follow_up_scheduled: true,
        follow_up_days: selectedDays,
        follow_up_date: followUpDate.toISOString(),
      },
    }, {
      onSuccess: () => {
        // Build update payload - track first follow-up time for SLA calculation
        const updates: Record<string, any> = {
          next_follow_up_date: followUpDate.toISOString(),
          status: 'needs_follow_up',
          call_status: 'callback_requested',
          last_contact_date: currentTime.toISOString(),
        };
        
        // Set first_follow_up_at if this is the first follow-up attempt
        if (!firstFollowUpAt) {
          updates.first_follow_up_at = currentTime.toISOString();
        }
        if (clearLostReason) {
          updates.lost_reason = null;
        }
        
        // Then update the lead
        updateLead.mutate({
          leadId,
          updates,
        }, {
          onSuccess: () => {
            // Reset and close
            setDescription('');
            setSelectedDays(null);
            onOpenChange(false);
          },
        });
      },
    });
  };

  const handleClose = () => {
    setDescription('');
    setSelectedDays(null);
    onOpenChange(false);
  };

  const followUpOptions = [
    { days: 1, label: 'Tomorrow' },
    { days: 3, label: 'In 3 days' },
    { days: 7, label: 'In 7 days' },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Schedule Follow-up
          </DialogTitle>
          <DialogDescription>
            Log what was discussed so the team can follow up effectively.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              What was discussed? <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="description"
              placeholder="e.g., Customer wants to wait until next week, prefers morning appointments..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px] resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Follow up in:
            </Label>
            <div className="flex gap-2">
              {followUpOptions.map((option) => (
                <Button
                  key={option.days}
                  type="button"
                  variant={selectedDays === option.days ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedDays(option.days)}
                  className="flex-1"
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!description.trim() || selectedDays === null || isSubmitting}
          >
            {isSubmitting ? 'Scheduling...' : 'Schedule Follow-up'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}